import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { PageTitles } from '@telus/d2c-core/lib/common';
import { INavigation, Navigation } from '@telus/d2c-core/models';

import { BehaviorSubject } from 'rxjs';
import { menuPages, adminPages } from '../../helpers';

interface ITabMenu {
  page: string;
  tabs: PageTitles[];
}

@Injectable({
  providedIn: 'root',
})
export class NavigationService {
  tabs = {};
  $history = new BehaviorSubject<Navigation[]>([]);
  $currentPage = new BehaviorSubject<Navigation>(null);

  private history: Navigation[] = [];
  private currentPage: Navigation;

  private pagesWithTabs: ITabMenu[] = [
    {
      page: PageTitles.vendor,
      tabs: [PageTitles.vendorGroup, PageTitles.vendorSalesRep],
    },
    {
      page: PageTitles.territoryWalklist,
      tabs: [
        PageTitles.territoryCreation,
        PageTitles.territoryManagement,
        PageTitles.territoryWalklistCreation,
        PageTitles.territoryWalklistAssigment,
      ],
    },
    {
      page: PageTitles.intel,
      tabs: [PageTitles.knock, PageTitles.drop],
    },
    {
      page: PageTitles.roleUser,
      tabs: [PageTitles.role, PageTitles.user],
    },
  ];

  constructor(private router: Router) {}

  add() {
    if (!this.router.url) {
      return;
    }

    this.addUrl(this.router.url);
    return this.currentPage;
  }

  addUrl(url: string) {
    const menu = menuPages.find((men) => url.startsWith(men.url));
    const menuAdmin = adminPages.find((men) => url.startsWith(men.url));

    const page = menu?.name || menuAdmin?.name;
    const urlParts = url.replace(menu?.url || menuAdmin?.url, '').split('/');

    let tab =
      urlParts.length === 4 || urlParts[2] === 'new' ? urlParts[1] : null;
    const id =
      urlParts.length === 0 || (urlParts.length === 1 && !urlParts[0])
        ? null
        : urlParts.length === 4
        ? urlParts[3]
        : urlParts[2];

    if (
      this.pagesWithTabs.map((pag) => pag.page).includes(page as PageTitles)
    ) {
      if (!tab) {
        const tabHistory = this.tabs[page];
        const tabs = this.getTabs(page);

        tab = tabHistory ? tabHistory : tabs[0];
      }
    }

    if (!this.urlExist(url, page, tab, id)) {
      this.addPageToHistory();
      this.saveCurrentPage({ url, page, tab, id });
    }
  }

  setTab(tab: PageTitles) {
    const page = this.pagesWithTabs.filter((menu) => menu.tabs.includes(tab))[0]
      ?.page;

    if (!page) {
      return;
    }

    this.tabs[page] = tab;
    const url =
      page === PageTitles.territoryWalklist
        ? `/app/${PageTitles.territoryWalklist}/${tab}`
        : `/app/${page}`;

    if (!this.urlExist(url, page, tab, null)) {
      this.addPageToHistory();
      this.saveCurrentPage({ url, page, tab });
    }
  }

  getTabs(page: PageTitles) {
    return this.pagesWithTabs.filter((menu) => menu.page === page)[0]?.tabs;
  }

  back() {
    let currentPage = this.currentPage.page;
    let url = this.defaultText();

    if (this.history.length > 0) {
      const oldPage = this.history.pop();
      currentPage = oldPage.page;
      url = oldPage.url;

      this.saveCurrentPage(oldPage);
      this.$history.next(this.history);
    } else {
      const { page, tab } = this.currentPage;
      this.saveCurrentPage({ url, page, tab });
    }

    this.router.navigate([url]);
  }

  go(page: PageTitles, tab?: PageTitles) {
    const url = `/app/${page}`;
    this.tabs[page] = tab;
    this.addUrl(url);

    this.router.navigate([url]);
  }

  getCurrentPage() {
    return this.currentPage;
  }

  private defaultText() {
    return `/app/${this.currentPage.page}`;
  }

  private urlExist(url, page, tab, id) {
    let exist = false;

    if (this.currentPage) {
      if (
        (this.currentPage.url === url ||
          `${this.currentPage.url}/${this.currentPage.tab}` === url) &&
        this.currentPage.page === page &&
        this.currentPage.tab === tab &&
        (this.currentPage.id === id || !this.currentPage.id)
      ) {
        exist = true;
      }
    }

    return exist;
  }

  private addPageToHistory() {
    if (this.currentPage) {
      this.history.push(this.currentPage);
      this.$history.next(this.history);
    }
  }

  private saveCurrentPage(navigation: INavigation) {
    this.currentPage = new Navigation(navigation);
    this.$currentPage.next(this.currentPage);
  }
}
